import React, { useState, useEffect, ReactNode } from 'react';
import { Edit, Trash, Eye, X, Search, CheckCircle2, Clock, AlertCircle, Loader2, LayoutDashboard, Download } from 'lucide-react';
import {
  fetchVendors,
  processVendorsForDisplay,
  deleteVendor,
  fetchVendorCommissionRate,
  updateVendorCommissionRate,
  VENDOR_EXPORT_FIELDS,
  exportVendorsToExcel,
} from '../../services/MasterAdmin/vendorService';
import { VendorDisplay, VendorLoginSnapshot, VendorExportFieldKey } from '../../models/MasterAdmin/vendor';
import FileGallery from '../../components/MasterAdmin/FileGallery';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { formatDoctorName } from '../../utils/doctorName';

const getDisplayVendorName = (vendor: { name: string; category: string }): string => {
  if (vendor.category === 'Doctor Clinic') {
    return formatDoctorName(vendor.name);
  }
  return vendor.name;
};

const formatOnboardDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

interface VendorsPageProps {
  employeeId?: string;
}

const VendorsPage: React.FC<VendorsPageProps> = ({ employeeId }) => {
  const [vendors, setVendors] = useState<VendorDisplay[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<VendorDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<VendorDisplay | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('All Categories');
  const [statusFilter, setStatusFilter] = useState<string>('All Status');
  const [searchText, setSearchText] = useState<string>('');
  const [timeSlotsFilter, setTimeSlotsFilter] = useState<string>('All');
  const [consultationChannelFilter, setConsultationChannelFilter] = useState<string>('All');
  const [specialityFilter, setSpecialityFilter] = useState<string>('All Specialities');
  const [availableSpecialities, setAvailableSpecialities] = useState<string[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<VendorDisplay | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [commissionVendor, setCommissionVendor] = useState<VendorDisplay | null>(null);
  const [commissionRateInput, setCommissionRateInput] = useState<string>('');
  const [referralCommissionInput, setReferralCommissionInput] = useState<string>('');
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState<VendorExportFieldKey[]>(
    VENDOR_EXPORT_FIELDS.filter((field) => field.defaultChecked).map((field) => field.key)
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const success = (location.state as {
      accountDeletionSuccess?: {
        doctorName: string;
        logoutForced?: boolean;
      };
    } | null)?.accountDeletionSuccess;

    if (!success?.doctorName) return;

    toast.success(
      `${success.doctorName} account deleted. ${
        success.logoutForced !== false
          ? 'Doctor logged out from all active sessions.'
          : 'Logout could not be confirmed.'
      } Appointments and settlements preserved.`,
      { duration: 6000, id: 'vendor-account-deletion-success' }
    );

    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  // Categories from the database
  const categories = [
    'All Categories',
    'Hospital', 
    'Ambulance', 
    'Medical Store',
    'Blood Bank',
    'Doctor Clinic',
    'Diagnostic Center',
    'Product Partner'
  ];

  // Status options
  const statuses = ['All Status', 'pending', 'approved', 'rejected'];

  useEffect(() => {
    const getVendors = async () => {
      try {
        setLoading(true);
        const response = await fetchVendors();
        let processedVendors = processVendorsForDisplay(response);
        if (employeeId) {
          processedVendors = processedVendors.filter(v => {
            const raw = v.originalData;
            const onboardingSubmittedBy =
              raw?.submitted_by ||
              raw?.VendorOnboarding?.submitted_by ||
              raw?.vendor?.Onboarding?.submitted_by ||
              raw?.Vendor?.Onboarding?.submitted_by;
            return onboardingSubmittedBy === employeeId;
          });
        }
        setVendors(processedVendors);
        setFilteredVendors(processedVendors);
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setError('Failed to load vendors. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    getVendors();
  }, [employeeId]);

  // Derive unique specialities from loaded doctor clinics
  useEffect(() => {
    const specs = new Set<string>();
    vendors.forEach(vendor => {
      if (vendor.category === 'Doctor Clinic') {
        const specList = vendor.originalData?.specializations;
        if (Array.isArray(specList)) {
          specList.forEach((s: any) => {
            if (typeof s === 'string' && s.trim()) {
              specs.add(s.trim());
            }
          });
        }
      }
    });
    setAvailableSpecialities(Array.from(specs).sort());
  }, [vendors]);

  // Reset doctor clinic filters when category changes
  useEffect(() => {
    if (categoryFilter !== 'Doctor Clinic') {
      setTimeSlotsFilter('All');
      setConsultationChannelFilter('All');
      setSpecialityFilter('All Specialities');
    }
  }, [categoryFilter]);

  // Apply filters whenever filter state changes
  useEffect(() => {
    let result = [...vendors];

    // Apply category filter
    if (categoryFilter !== 'All Categories') {
      result = result.filter(vendor => vendor.category === categoryFilter);
    }

    // Apply status filter
    if (statusFilter !== 'All Status') {
      result = result.filter(vendor => vendor.status.toLowerCase() === statusFilter.toLowerCase());
    }

    // Apply doctor-specific filters (only active when Doctor Clinic category is selected)
    if (categoryFilter === 'Doctor Clinic') {
      // Time slots filter
      if (timeSlotsFilter !== 'All') {
        if (timeSlotsFilter === 'With Time Slots') {
          result = result.filter(vendor => vendor.hasTimeSlots);
        } else if (timeSlotsFilter === 'Without Time Slots') {
          result = result.filter(vendor => !vendor.hasTimeSlots);
        }
      }

      // Consultation Channel filter
      if (consultationChannelFilter !== 'All') {
        result = result.filter(vendor => {
          const types = vendor.originalData?.consultationTypes;
          if (Array.isArray(types)) {
            return types.some((t: any) => String(t || '').toLowerCase() === consultationChannelFilter.toLowerCase());
          }
          return false;
        });
      }

      // Speciality filter
      if (specialityFilter !== 'All Specialities') {
        result = result.filter(vendor => {
          const specs = vendor.originalData?.specializations;
          if (Array.isArray(specs)) {
            return specs.some((s: any) => String(s || '').trim() === specialityFilter);
          }
          return false;
        });
      }
    }

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(
        vendor => 
          vendor.name.toLowerCase().includes(searchLower) ||
          vendor.email.toLowerCase().includes(searchLower) ||
          vendor.phone.toLowerCase().includes(searchLower) ||
          vendor.address.toLowerCase().includes(searchLower)
      );
    }

    setFilteredVendors(result);
  }, [vendors, categoryFilter, statusFilter, searchText, timeSlotsFilter, consultationChannelFilter, specialityFilter]);

  const handleViewVendor = (vendor: VendorDisplay) => {
    if (vendor.category === 'Hospital') {
      navigate(`/vendors/hospital/${vendor.id}`);
    } else if (vendor.category === 'Ambulance') {
      navigate(`/vendors/ambulance/${vendor.id}`);
    } else if (vendor.category === 'Medical Store') {
      navigate(`/vendors/medical-store/${vendor.id}`);
    } else if (vendor.category === 'Blood Bank') {
      navigate(`/vendors/blood-bank/${vendor.id}`);
    } else if (vendor.category === 'Doctor Clinic') {
      navigate(`/vendors/doctor-clinic/${vendor.id}`);
    } else if (vendor.category === 'Diagnostic Center') {
      navigate(`/vendors/diagnostic-center/${vendor.id}`);
    } else if (vendor.category === 'Product Partner') {
      navigate(`/vendors/product/${vendor.id}`);
    } else {
      setSelectedVendor(vendor);
    }
  };

  const closeModal = () => {
    setSelectedVendor(null);
  };

  const handleViewDashboard = (vendor: VendorDisplay) => {
    if (vendor.category === 'Hospital') {
      navigate(`/vendors/hospital/${vendor.id}`);
    } else if (vendor.category === 'Ambulance') {
      navigate(`/vendors/ambulance/${vendor.id}`);
    } else if (vendor.category === 'Medical Store') {
      navigate(`/vendors/medical-store/${vendor.id}`);
    } else if (vendor.category === 'Blood Bank') {
      navigate(`/vendors/blood-bank/${vendor.id}`);
    } else if (vendor.category === 'Doctor Clinic') {
      navigate(`/vendors/doctor-clinic/${vendor.id}`);
    } else if (vendor.category === 'Diagnostic Center') {
      navigate(`/vendors/diagnostic-center/${vendor.id}`);
    } else if (vendor.category === 'Product Partner') {
      navigate(`/vendors/product/${vendor.id}`);
    }
  };

  const handleEditVendor = (vendor: VendorDisplay) => {
    const baseVendor = vendor.originalData?.vendor || vendor.originalData?.Vendor;
    const vendorData = {
      vendor: baseVendor,
      fullProfile: vendor.originalData
    };

    if (vendor.category === 'Hospital') {
      navigate(`/vendors/edit/hospital/${vendor.id}`, { state: { vendorData } });
    } else if (vendor.category === 'Ambulance') {
      navigate(`/vendors/edit/ambulance/${vendor.id}`, { state: { vendorData } });
    } else if (vendor.category === 'Medical Store') {
      navigate(`/vendors/edit/medical-store/${vendor.id}`, { state: { vendorData } });
    } else if (vendor.category === 'Blood Bank') {
      navigate(`/vendors/edit/blood-bank/${vendor.id}`, { state: { vendorData } });
    } else if (vendor.category === 'Doctor Clinic') {
      navigate(`/vendors/edit/clinic/${vendor.id}`, { state: { vendorData } });
    } else if (vendor.category === 'Diagnostic Center') {
      navigate(`/vendors/edit/diagnostic-center/${vendor.id}`, { state: { vendorData } });
    } else if (vendor.category === 'Product Partner') {
      navigate(`/vendors/edit/product-partner/${vendor.id}`, { state: { vendorData } });
    }
  };

  // Get status badge color and icon
  const getStatusDisplay = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'approved':
        return {
          color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
          icon: <CheckCircle2 className="w-4 h-4 mr-1" />,
          text: 'Approved'
        };
      case 'pending':
        return {
          color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
          icon: <Clock className="w-4 h-4 mr-1" />,
          text: 'Pending'
        };
      case 'rejected':
        return {
          color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
          icon: <AlertCircle className="w-4 h-4 mr-1" />,
          text: 'Rejected'
        };
      default:
        return {
          color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
          icon: null,
          text: status.charAt(0).toUpperCase() + status.slice(1)
        };
    }
  };

  /** Vendor.isActive from backend (account enabled / disabled) */
  const getVendorActiveStatusDisplay = (isActive: boolean) => ({
    color: isActive
      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    text: isActive ? 'Active' : 'Inactive',
  });

  const formatPlatforms = (platforms: string[] | undefined) => {
    if (!platforms?.length) return '—';
    return platforms
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(', ');
  };

  /** Backend: LOGGED_IN, LOGGED_OUT, EXPIRED, REVOKED, ACTIVE, NONE */
  const getLoginStatusDisplay = (snapshot: VendorLoginSnapshot | null | undefined) => {
    if (!snapshot) {
      return {
        color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
        text: '—',
      };
    }
    const s = snapshot.loginStatus;
    switch (s) {
      case 'LOGGED_IN':
        return {
          color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
          text: 'Logged in',
        };
      case 'LOGGED_OUT':
        return {
          color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
          text: 'Logged out',
        };
      case 'EXPIRED':
        return {
          color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
          text: 'Expired',
        };
      case 'REVOKED':
        return {
          color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
          text: 'Revoked',
        };
      case 'ACTIVE':
        return {
          color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
          text: 'Active session',
        };
      case 'NONE':
        return {
          color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
          text: 'No login data',
        };
      default:
        return {
          color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
          text: s.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
        };
    }
  };

  // Check if a field contains image or file URLs
  const isFileGalleryField = (key: string): boolean => {
    const fileFields = [
      'photos', 'images', 'certificates', 'licenses', 'licenseFiles', 
      'registrationCertificateFiles', 'agencyPhotos', 'centerPhotosUrl', 
      'profilePicture', 'clinicPhotos', 'trainingCertifications', 
      'officePhotos', 'medicalLicenseFile', 'regulatoryComplianceUrl',
      'qualityAssuranceUrl', 'filesAndImages', 'complianceCertificates',
      'registrationCertificates', 'certifications'
    ];
    return fileFields.includes(key);
  };

  // Format a value for display based on its type
  const formatValue = (key: string, value: any): ReactNode | string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // If it's a file gallery field
    if (isFileGalleryField(key)) {
      // Process array of Firebase storage URLs
      if (Array.isArray(value)) {
        // If array contains objects with url property (hospital certifications format)
        if (value.length > 0 && typeof value[0] === 'object' && 'url' in value[0]) {
          return <FileGallery files={value} />;
        }
        
        // If array contains string URLs (registration certificates format)
        if (value.length > 0 && typeof value[0] === 'string') {
          return <FileGallery files={value} />;
        }
        
        // For empty arrays or other formats
        if (value.length === 0) {
          return 'No files';
        }
      }
      
      // Handle JSON string that might be an array of URLs
      if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
        try {
          const parsedValue = JSON.parse(value);
          if (Array.isArray(parsedValue)) {
            return <FileGallery files={parsedValue} />;
          }
          if (typeof parsedValue === 'object') {
            const fileArray = Object.entries(parsedValue).map(([name, url]) => ({
              name,
              url: typeof url === 'string' ? url : ''
            }));
            return <FileGallery files={fileArray} />;
          }
        } catch (error) {
          // If not a valid JSON, treat as a single URL
          console.error('Error parsing JSON:', error);
        }
      }
      
      // If it's a single file URL
      if (typeof value === 'string') {
        return <FileGallery files={[value]} />;
      }
      
      // If it's a JSON object that might contain files
      if (typeof value === 'object') {
        try {
          const fileArray = Object.entries(value).map(([name, url]) => ({
            name,
            url: typeof url === 'string' ? url : ''
          }));
          return <FileGallery files={fileArray} />;
        } catch (error) {
          return JSON.stringify(value);
        }
      }
    }

    // Default formatting
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return 'Complex object';
      }
    }
    return String(value);
  };

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      
      // Make the API call first
      const response = await api.patch(`/master-admin/vendors/${vendorId}/toggle-status`);
      
      // Only update the state if the API call was successful
      if (response.data.success) {
        // Get the new status from the API response
        const newStatus = response.data.data.status;
        
        // Update both vendors and filteredVendors lists
        const updateVendorStatus = (vendor: VendorDisplay) => 
          vendor.id === vendorId 
            ? { ...vendor, status: newStatus }
            : vendor;

        setVendors(prevVendors => prevVendors.map(updateVendorStatus));
        setFilteredVendors(prevVendors => prevVendors.map(updateVendorStatus));
        
        // Update selected vendor if it's the one being modified
        if (selectedVendor?.id === vendorId) {
          setSelectedVendor(prev => prev ? { ...prev, status: newStatus } : null);
        }
      } else {
        // If the API call was not successful, show an error
        setError('Failed to update vendor status. Please try again.');
      }
    } catch (error) {
      console.error('Error updating vendor status:', error);
      setError('Failed to update vendor status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteClick = (vendor: VendorDisplay, e: React.MouseEvent) => {
    e.stopPropagation();
    setVendorToDelete(vendor);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!vendorToDelete) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      const response = await deleteVendor(vendorToDelete.id);
      
      if (response.success) {
        // Remove the deleted vendor from the state
        setVendors(prevVendors => prevVendors.filter(v => v.id !== vendorToDelete.id));
        setFilteredVendors(prevVendors => prevVendors.filter(v => v.id !== vendorToDelete.id));
        
        // Close the modal
        setIsDeleteModalOpen(false);
        setVendorToDelete(null);
      } else {
        setDeleteError(response.message || 'Failed to delete vendor');
      }
    } catch (error) {
      console.error('Error deleting vendor:', error);
      const serverMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error;
      setDeleteError(serverMessage || 'An error occurred while deleting the vendor');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setVendorToDelete(null);
    setDeleteError(null);
  };

  const supportsCommissionRate = (vendor: VendorDisplay | null) =>
    !!vendor && (vendor.category === 'Ambulance' || vendor.category === 'Medical Store');

  const openCommissionModal = async (vendor: VendorDisplay) => {
    if (!supportsCommissionRate(vendor)) return;
    setCommissionVendor(vendor);
    setIsCommissionModalOpen(true);
    setCommissionError(null);
    setCommissionLoading(true);
    try {
      const response = await fetchVendorCommissionRate(vendor.id);
      setCommissionRateInput(String(response?.data?.commissionRate ?? 0));
      setReferralCommissionInput(String(response?.data?.referralCommission ?? 0));
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to load current commission rate');
      setCommissionRateInput('');
      setReferralCommissionInput('');
    } finally {
      setCommissionLoading(false);
    }
  };

  const closeCommissionModal = () => {
    if (commissionSaving) return;
    setIsCommissionModalOpen(false);
    setCommissionVendor(null);
    setCommissionRateInput('');
    setReferralCommissionInput('');
    setCommissionError(null);
    setCommissionLoading(false);
  };

  const toggleExportField = (fieldKey: VendorExportFieldKey) => {
    setSelectedExportFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((key) => key !== fieldKey) : [...prev, fieldKey]
    );
  };

  const handleSelectAllExportFields = () => {
    setSelectedExportFields(VENDOR_EXPORT_FIELDS.map((field) => field.key));
  };

  const handleClearExportFields = () => {
    setSelectedExportFields([]);
  };

  const handleDownloadVendors = () => {
    if (!selectedExportFields.length) return;
    exportVendorsToExcel(filteredVendors, selectedExportFields);
    setIsExportModalOpen(false);
  };

  const handleSaveCommissionRate = async () => {
    if (!commissionVendor) return;
    const parsed = Number(commissionRateInput);
    const referralParsed = Number(referralCommissionInput);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCommissionError('Please enter a valid commission rate between 0 and 100.');
      return;
    }
    if (Number.isNaN(referralParsed) || referralParsed < 0) {
      setCommissionError('Please enter a valid referral commission amount (0 or more).');
      return;
    }
    const actionText = Number(commissionRateInput) > 0 ? 'update' : 'set';
    const confirmed = window.confirm(
      `Are you sure you want to ${actionText} commission rate to ${parsed.toFixed(2)}% for ${getDisplayVendorName(commissionVendor)}?`
    );
    if (!confirmed) return;

    try {
      setCommissionSaving(true);
      setCommissionError(null);
      await updateVendorCommissionRate(commissionVendor.id, {
        commissionRate: parsed,
        referralCommission: referralParsed,
      });
      // Keep local copy in sync for modal details section
      const updater = (vendor: VendorDisplay) =>
        vendor.id === commissionVendor.id
          ? {
              ...vendor,
              originalData: {
                ...vendor.originalData,
                commissionRate: parsed,
              },
            }
          : vendor;
      setVendors((prev) => prev.map(updater));
      setFilteredVendors((prev) => prev.map(updater));
      if (selectedVendor?.id === commissionVendor.id) {
        setSelectedVendor((prev) =>
          prev
            ? { ...prev, originalData: { ...prev.originalData, commissionRate: parsed } }
            : null
        );
      }
      closeCommissionModal();
    } catch (error: any) {
      setCommissionError(error?.response?.data?.message || 'Failed to update commission rate');
    } finally {
      setCommissionSaving(false);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700 space-y-4">
          <div className="flex flex-row items-center justify-between">
            <h3 className="text-lg font-semibold dark:text-white">All Vendors</h3>
            
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => navigate('/vendors/onboarding/add/clinic')}
                className="flex items-center px-3 py-1.5 border border-primary-600 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors shadow-sm"
              >
                + Add Vendor (Clinic)
              </button>
              <button
                type="button"
                onClick={() => setIsExportModalOpen(true)}
                disabled={filteredVendors.length === 0}
                className="flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm dark:text-gray-300 disabled:cursor-not-allowed disabled:opacity-50 transition-colors shadow-sm"
              >
                <Download className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                Download Vendor Data
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center pt-2">
            {/* Search box */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full min-w-[200px] border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            
            {/* Category filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {statuses.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>

            {categoryFilter === 'Doctor Clinic' && (
              <>
                {/* Speciality filter */}
                <select
                  value={specialityFilter}
                  onChange={(e) => setSpecialityFilter(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="All Specialities">All Specialities</option>
                  {availableSpecialities.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>

                {/* Consultation Channel filter */}
                <select
                  value={consultationChannelFilter}
                  onChange={(e) => setConsultationChannelFilter(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="All">All Consultation Channels</option>
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                  <option value="Instant">Instant</option>
                </select>

                {/* Time Slots filter */}
                <select
                  value={timeSlotsFilter}
                  onChange={(e) => setTimeSlotsFilter(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="All">All Time Slots</option>
                  <option value="With Time Slots">With Time Slots</option>
                  <option value="Without Time Slots">Without Time Slots</option>
                </select>
              </>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="ma-table-scroll scrollbar-light">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Vendor Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Account status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Active status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Time Slots
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Login status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Onboard Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td colSpan={10} className="px-6 py-12">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full border-4 border-primary-200 dark:border-primary-800"></div>
                        <div className="w-12 h-12 rounded-full border-4 border-primary-600 dark:border-primary-400 border-t-transparent animate-spin absolute top-0 left-0"></div>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Loading vendors...</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                Try again
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="ma-table-scroll scrollbar-light">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Vendor Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Account status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Active status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Time Slots
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Login status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Onboard Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredVendors.length > 0 ? (
                    filteredVendors.map((vendor) => (
                      <tr 
                        key={vendor.id} 
                        className="relative border-l-2 border-l-transparent hover:border-l-primary-500 dark:hover:border-l-primary-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:shadow-sm dark:hover:shadow-gray-800 transition-all duration-150 cursor-pointer"
                        onClick={() => handleViewVendor(vendor)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{getDisplayVendorName(vendor)}</div>
                          {vendor.category === 'Doctor Clinic' && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xs truncate" title={(vendor.originalData?.specializations || []).join(', ')}>
                              <span className="font-medium text-gray-600 dark:text-gray-400">
                                {(vendor.originalData?.specializations || []).join(', ') || 'No specialities'}
                              </span>
                              {vendor.originalData?.consultationTypes?.length > 0 && (
                                <span className="text-gray-400 dark:text-gray-500 ml-1.5 border-l pl-1.5 dark:border-gray-700">
                                  {vendor.originalData.consultationTypes.join('/')}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                            {vendor.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {vendor.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {vendor.phone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusDisplay(vendor.status).color}`}>
                              {getStatusDisplay(vendor.status).icon}
                              {getStatusDisplay(vendor.status).text}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getVendorActiveStatusDisplay(vendor.isActive).color}`}
                            title="From Vendor account (isActive)"
                          >
                            {getVendorActiveStatusDisplay(vendor.isActive).text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {vendor.category === 'Doctor Clinic' ? (
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                vendor.hasTimeSlots
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                              }`}
                            >
                              {vendor.hasTimeSlots ? 'Added' : 'Not Added'}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getLoginStatusDisplay(vendor.loginSnapshot).color}`}
                            title={
                              vendor.loginSnapshot && vendor.loginSnapshot.activeSessionCount > 1
                                ? `${vendor.loginSnapshot.activeSessionCount} active sessions`
                                : undefined
                            }
                          >
                            {getLoginStatusDisplay(vendor.loginSnapshot).text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 max-w-[10rem] truncate" title={formatPlatforms(vendor.loginSnapshot?.platforms)}>
                          {formatPlatforms(vendor.loginSnapshot?.platforms)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatOnboardDateTime(vendor.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVendor(vendor);
                              }}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {vendor.category === 'Hospital' || vendor.category === 'Ambulance' || vendor.category === 'Medical Store' || vendor.category === 'Blood Bank' || vendor.category === 'Doctor Clinic' || vendor.category === 'Diagnostic Center' || vendor.category === 'Product Partner' ? (
                              <button
                                onClick={() => handleViewDashboard(vendor)}
                                className="p-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                                title="View Dashboard"
                              >
                                <LayoutDashboard className="w-4 h-4" />
                              </button>
                            ) : null}
                            <button 
                              className="p-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditVendor(vendor);
                              }}
                              title="Edit Vendor"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                              onClick={(e) => handleDeleteClick(vendor, e)}
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                        No vendors found matching the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="flex flex-col gap-3 border-t p-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing <span className="font-medium">{filteredVendors.length}</span> results
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                  Previous
                </button>
                <button className="px-3 py-1 border border-primary-600 rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700">
                  1
                </button>
                <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Vendor Detail Modal */}
      {selectedVendor && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 shadow-xl max-w-4xl w-full max-h-[min(92dvh,calc(100vh-1rem))] overflow-hidden rounded-t-xl sm:rounded-lg">
            <div className="flex flex-wrap justify-between items-center gap-2 border-b dark:border-gray-700 p-3 sm:p-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Vendor Details
                </h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusDisplay(selectedVendor.status).color}`}>
                  {getStatusDisplay(selectedVendor.status).text}
                </span>
              </div>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(min(92dvh,calc(100vh-1rem))-7rem)] sm:max-h-[calc(85vh-4rem)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Profile Section */}
                <div className="col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="h-16 w-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                      {selectedVendor.profilePicture ? (
                        <img
                          src={selectedVendor.profilePicture}
                          alt={getDisplayVendorName(selectedVendor)}
                          className="h-16 w-16 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl text-primary-800 dark:text-primary-300 font-medium">
                          {getDisplayVendorName(selectedVendor).charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{getDisplayVendorName(selectedVendor)}</h3>
                      <p className="text-gray-500 dark:text-gray-400">{selectedVendor.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStatus(selectedVendor.id, selectedVendor.status);
                    }}
                    disabled={updatingStatus === selectedVendor.id}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                      selectedVendor.status.toLowerCase() === 'approved'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {updatingStatus === selectedVendor.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        {selectedVendor.status.toLowerCase() === 'approved' ? (
                          <>
                            <Clock className="w-4 h-4" />
                            <span>Mark as Pending</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Mark as Approve</span>
                          </>
                        )}
                      </>
                    )}
                  </button>
                  {supportsCommissionRate(selectedVendor) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openCommissionModal(selectedVendor);
                      }}
                      className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50"
                    >
                      Commission Rate
                    </button>
                  )}
                </div>

                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Category</label>
                      <span className="mt-1 block px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                        {selectedVendor.category}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Phone</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{selectedVendor.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Address</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{selectedVendor.address}</p>
                    </div>
                  </div>
                </div>

                {/* Status Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Status Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Account status</label>
                      <div className="mt-1">
                        <span className={`px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getStatusDisplay(selectedVendor.status).color}`}>
                          {getStatusDisplay(selectedVendor.status).icon}
                          {getStatusDisplay(selectedVendor.status).text}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Active status</label>
                      <span className={`mt-1 block px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getVendorActiveStatusDisplay(selectedVendor.isActive).color}`}>
                        {getVendorActiveStatusDisplay(selectedVendor.isActive).text}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Login status</label>
                      <span className={`mt-1 block px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getLoginStatusDisplay(selectedVendor.loginSnapshot).color}`}>
                        {getLoginStatusDisplay(selectedVendor.loginSnapshot).text}
                        {selectedVendor.loginSnapshot && selectedVendor.loginSnapshot.activeSessionCount > 1
                          ? ` (${selectedVendor.loginSnapshot.activeSessionCount} devices)`
                          : ''}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Platform</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{formatPlatforms(selectedVendor.loginSnapshot?.platforms)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Onboard Date & Time</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{formatOnboardDateTime(selectedVendor.createdAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Vendor ID</label>
                      <p className="mt-1 font-mono text-sm text-gray-900 dark:text-white">{selectedVendor.id}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Additional Details Section */}
              <div className="mt-8 pt-6 border-t dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                  Additional Details
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(selectedVendor.originalData)
                    .filter(([key]) => !['vendorId', 'generatedId', 'vendorDetails', 'Vendor'].includes(key))
                    .map(([key, value]) => {
                      if (
                        value === null || 
                        value === undefined || 
                        (Array.isArray(value) && value.length === 0) ||
                        (typeof value === 'object' && !isFileGalleryField(key) && Object.keys(value).length === 0)
                      ) {
                        return null;
                      }

                      return (
                        <div key={key} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </h5>
                          <div className="text-sm text-gray-900 dark:text-white">
                            {formatValue(key, value)}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            
            <div className="border-t dark:border-gray-700 p-4 flex justify-end space-x-3">
              <button 
                onClick={closeModal}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => handleEditVendor(selectedVendor)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {isCommissionModalOpen && commissionVendor && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Commission Rate</h3>
              <button
                onClick={closeCommissionModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {getDisplayVendorName(commissionVendor)} ({commissionVendor.category})
              </p>
              {commissionLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading current rate...</p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={commissionRateInput}
                      onChange={(e) => setCommissionRateInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                      placeholder="Enter commission rate"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Referral Commission Amount (INR)
                    </label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={referralCommissionInput}
                      onChange={(e) => setReferralCommissionInput(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                      placeholder="Enter referral commission amount"
                    />
                  </div>
                </>
              )}
              {commissionError && (
                <p className="text-sm text-red-600 dark:text-red-400">{commissionError}</p>
              )}
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeCommissionModal}
                disabled={commissionSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCommissionRate}
                disabled={commissionLoading || commissionSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2"
              >
                {commissionSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {commissionSaving ? 'Saving...' : 'Add / Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isExportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Download Vendor Data</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Choose fields to include. Export uses the current filtered list ({filteredVendors.length} vendors).
                </p>
              </div>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <button
                  type="button"
                  onClick={handleSelectAllExportFields}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={handleClearExportFields}
                  className="text-gray-600 dark:text-gray-300 hover:underline"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
                {VENDOR_EXPORT_FIELDS.map((field) => (
                  <label
                    key={field.key}
                    className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedExportFields.includes(field.key)}
                      onChange={() => toggleExportField(field.key)}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span>{field.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadVendors}
                disabled={!selectedExportFields.length || filteredVendors.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Excel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && vendorToDelete && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirm Deletion
                </h3>
                <button 
                  onClick={closeDeleteModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-300">
                  Are you sure you want to delete <span className="font-semibold">{getDisplayVendorName(vendorToDelete)}</span>? This action cannot be undone.
                </p>
                {deleteError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {deleteError}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsPage; 